import axios from 'axios';
import { store } from '../store/store';
import { Logout, loginConfirmedAction } from '../store/actions/AuthActions';
import { refreshTokenApi, saveTokenInLocalStorage, loadTokenFromLocalStorage } from './AuthService';

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
});

axiosInstance.interceptors.request.use((config) => {
    const state = store.getState();
    const { access, customerNumber } = state.auth.auth;
    const originalUrl = config.url || '';
    const normalizedUrl = originalUrl.startsWith('/') ? originalUrl : `/${originalUrl}`;

    if (access) {
        config.headers['Authorization'] = `Bearer ${access}`;
    }

    // Prepend tenant prefix to all non-auth requests
    if (customerNumber && !normalizedUrl.startsWith('/auth/') && !normalizedUrl.startsWith('/c/') && !normalizedUrl.startsWith('/global/')) {
        config.url = `/c/${customerNumber}${normalizedUrl}`;
    } else {
        config.url = normalizedUrl;
    }

    return config;
});

// ── Token refresh on 401 ───────────────────────────────────────────────────────
// When an access token expires, silently refresh and replay the original request.
// Multiple simultaneous 401s are queued behind a single refresh call.

let isRefreshing = false;
let refreshQueue = [];  // [{ resolve, reject }]

function drainQueue(error, token = null) {
    refreshQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
    refreshQueue = [];
}

function forceLogout() {
    store.dispatch(Logout(null));
    window.location.href = '/login';
}

axiosInstance.interceptors.response.use(
    response => response,
    error => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        // Don't try to refresh if the token endpoint itself returned 401
        if ((originalRequest.url || '').includes('/auth/token/')) {
            forceLogout();
            return Promise.reject(error);
        }

        // While a refresh is already in flight, queue this request
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                refreshQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axiosInstance(originalRequest);
            });
        }

        const refresh = store.getState().auth.auth.refresh;
        if (!refresh) {
            forceLogout();
            return Promise.reject(error);
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return refreshTokenApi(refresh)
            .then(({ data }) => {
                // Persist new access token
                const existing = loadTokenFromLocalStorage() || {};
                const updated = { ...existing, access: data.access };
                saveTokenInLocalStorage(updated);

                // Sync Redux store (keep all existing user/tenant fields)
                const cur = store.getState().auth.auth;
                store.dispatch(loginConfirmedAction({
                    ...updated,
                    username:       cur.username,
                    full_name:      cur.full_name,
                    user_id:        cur.user_id,
                    photo_filename: cur.photo_filename,
                    customer_number: cur.customerNumber,
                    tenant_config:  cur.tenantConfig,
                }));

                drainQueue(null, data.access);
                originalRequest.headers['Authorization'] = `Bearer ${data.access}`;
                return axiosInstance(originalRequest);
            })
            .catch(err => {
                drainQueue(err);
                forceLogout();
                return Promise.reject(err);
            })
            .finally(() => {
                isRefreshing = false;
            });
    },
);

export default axiosInstance;
